import { ErrorDisplay } from "../../../../../../core/api/errorTypes"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import {
    fetchOccupancyRate,
    fetchOccupancyRatePerResource,
    ServiceCenterOccupancyRateReportResponse,
    ServiceCenterStatisticsQuery,
} from "../../statisticsApi"
import { OccupancyRateEntry, OccupancyRateSummary } from "../../statisticsTypes"
import { ResourceTypeFanoutData, useResourceTypeFanout } from "../resourceDashboard/useResourceTypeFanout"

const selectOccupancies = (data: ServiceCenterOccupancyRateReportResponse): OccupancyRateEntry[] =>
    data?.occupancyRates ?? []

type OccupancyDashboardParams = Readonly<{
    serviceCenterId: number
    startDate: string
    endDate: string
}>

export type OccupancyDashboardData = ResourceTypeFanoutData<OccupancyRateEntry> &
    Readonly<{
        summary: OccupancyRateSummary | undefined
    }>

/**
 * Loads the occupancy data for the service-center dashboard: a per-type fan-out feeds the tables and
 * the summary chart, and the aggregate occupancy-rate endpoint drives the hero numbers (with a
 * client-side fallback derived from the per-resource data when it is unavailable).
 */
export function useOccupancyDashboardData({
    serviceCenterId,
    startDate,
    endDate,
}: OccupancyDashboardParams): OccupancyDashboardData {
    const enabled = serviceCenterId > 0

    const fanout = useResourceTypeFanout({
        fetch: fetchOccupancyRatePerResource,
        queryName: ServiceCenterStatisticsQuery.SERVICE_CENTER_STATISTICS_OCCUPANCY_RATE_PER_RESOURCE,
        select: selectOccupancies,
        serviceCenterId,
        startDate,
        endDate,
        enabled,
    })

    // Aggregate endpoint for hero numbers. Errors are swallowed (ErrorDisplay.NONE) because the
    // dashboard derives the same numbers from the per-resource data as a fallback.
    const summaryResult = useApiQuery(fetchOccupancyRate, {
        queryName: ServiceCenterStatisticsQuery.SERVICE_CENTER_STATISTICS_OCCUPANCY_RATE,
        dependencies: { serviceCenterId, startDate, endDate },
        errorDisplay: ErrorDisplay.NONE,
        enabled,
    })

    return { ...fanout, summary: summaryResult.data }
}
